import React, { useEffect } from 'react';
import { toFlag } from 'pano-utils';
import type { Draft } from 'immer';
import { useGate } from 'effector-react';
import {
  AutoRotateAction,
  AutoRotateType,
  type SceneConfigAutoRotate,
  SceneType,
} from '@types';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import { Collapse } from '@components/Collapse';
import { $sceneMapApi } from '@stores/dataStores';
import Slider from '@components/Slider';
import FeatureToggle from '@components/FeatureToggle';
import { MainPlayer } from '@stores/krpPlayerStores';
import useToggle from '@hooks/useToggle';
import { $GateRotateEditMode } from '@stores/autoRotateStores';
import SidebarApplyTo from '@modules/sidebar/components/SidebarApplyTo';
import { sendSuccess } from '@components/Noti';
import { showSceneModal } from '@modules/category';
import { krpRemoveCustomRotate } from '../actions/krpRotate';
import { CustomRotate } from './CustomRotate';
import type { SidebarAutoRotateProps } from './types';

const typeItems: DropdownItem<AutoRotateType>[] = [
  { id: AutoRotateType.right, name: '向右旋转' },
  { id: AutoRotateType.left, name: '向左旋转' },
  { id: AutoRotateType.none, name: '不旋转' },
  { id: AutoRotateType.custom, name: '自定义' },
];
export function SidebarAutoRotatePano({
  sceneId,
  data,
  sceneView,
}: SidebarAutoRotateProps) {
  const { type, duration, holdView, stayTime, tourData } = data;
  const editMode = useToggle(false);
  useGate($GateRotateEditMode, editMode.isOn);

  useEffect(() => {
    MainPlayer.get()?.updateAutoRotate({
      horizon: holdView ? sceneView.vLookAt : 'off',
      tofov: holdView ? sceneView.fov : 'off',
    });
    if (type === AutoRotateType.none) {
      MainPlayer.get()?.autoRotateStop();
    } else if (type === AutoRotateType.right) {
      MainPlayer.get()?.autoRotateStart(duration ?? 0);
    } else if (type === AutoRotateType.left) {
      MainPlayer.get()?.autoRotateStart(-(duration ?? 0));
    }
    return () => {
      MainPlayer.get()?.autoRotateStop();
    };
  }, [type, duration, holdView]);

  const updateAutoRotate = (
    updater: (
      draft: Draft<SceneConfigAutoRotate>
    ) => SceneConfigAutoRotate | void
  ) => {
    $sceneMapApi.updateScene({
      id: sceneId,
      updater: (draft) => {
        updater(draft.config.autoRotate!);
      },
    });
  };

  const handleTypeChange = (typeId: AutoRotateType) => {
    updateAutoRotate((draft) => {
      draft.type = typeId;
      // 向左/向右互相切换时，不修改其它值，否则部分值设为默认
      if (
        (typeId === AutoRotateType.left && type === AutoRotateType.right) ||
        (typeId === AutoRotateType.right && type === AutoRotateType.left)
      ) {
        return;
      }
      if (type === AutoRotateType.custom) {
        krpRemoveCustomRotate();
      }
      draft.duration = 180;
      draft.stayTime = 180;
      draft.holdView = 1;
      draft.rotateAction =
        typeId === AutoRotateType.none
          ? AutoRotateAction.byTime
          : AutoRotateAction.byRound;
      draft.tourData = [];
    });
  };

  const handleDurChange = (val: number) => {
    updateAutoRotate((draft) => {
      draft.duration = val;
    });
  };

  const handleHoldViewToggle = (val: boolean) => {
    updateAutoRotate((draft) => {
      draft.holdView = toFlag(val);
    });
  };

  const handleStayTimeChange = (val: number) => {
    updateAutoRotate((draft) => {
      draft.stayTime = val;
    });
  };

  const handleApplyTo = async () => {
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      subTitle: '注：只能应用到同类型场景中',
      allowedTypes: [SceneType.pano],
      multiple: true,
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        draft.get(_id)!.config.autoRotate = {
          ...draft.get(_id)!.config.autoRotate,
          type,
          duration,
          stayTime,
          holdView,
          tourData,
        };
      });
    });
    sendSuccess('导览方式已应用到所选场景');
  };

  return (
    <Collapse title="导览方式">
      <Dropdown
        className="mb-20"
        selected={type}
        items={typeItems}
        onChange={handleTypeChange}
      />
      {(type === AutoRotateType.left || type === AutoRotateType.right) && (
        <>
          <Slider
            min={30}
            max={360}
            valToString={(val) => `${val}秒/圈`}
            value={duration}
            onChange={handleDurChange}
          >
            <Slider.Header title="旋转速度" />
            <Slider.Bar />
            <Slider.Control />
          </Slider>
          <FeatureToggle
            title="自动巡游时，保持初始视角"
            isOn={!!holdView}
            onToggle={handleHoldViewToggle}
          />
        </>
      )}
      {type === AutoRotateType.none && (
        <Slider
          min={3}
          max={720}
          valToString={(val) => `${val}秒`}
          value={stayTime}
          onChange={handleStayTimeChange}
        >
          <Slider.Header title="停留时长" />
          <Slider.Bar />
          <Slider.Control />
        </Slider>
      )}
      {type === AutoRotateType.custom && <CustomRotate />}
      <SidebarApplyTo
        title="把导览方式应用到..."
        className="mt-20"
        onClick={handleApplyTo}
      />
    </Collapse>
  );
}
